import { inject } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert, isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { Observable } from 'rxjs';
import { AnnotationTransientState } from '../../../cross-team-shared/annotation/shared/state/annotation-state';
import { ObjectState } from '../../../cross-team-shared/annotation/shared/state/object-state';
import { ShapeState } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { getShapesFromShapeInfo } from '../../h3ml/state/task-workspace-state.utils';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { ObjectScopingService } from './object-scoping.service';

export abstract class AbstractAnnotationTransientInfoUpdateService<
  TransientValueKey extends keyof AnnotationTransientState,
> extends InjectableSuperclass {
  protected taskWorkspaceStore = inject(TaskWorkspaceStore);
  protected objectScopingService = inject(ObjectScopingService);

  protected abstract transientKey: TransientValueKey;

  protected subscribeToChangesUpdatingTransientValue() {
    this.subscribeTo(this.getObjectIdsChanged$(), (objectIdsWithChange) => {
      const { objects } = this.taskWorkspaceStore.state();

      // we want to start with the top level affected objects because those can
      // affect the children. we will then work our way down. this allows not to
      // update an object and its children twice, for example if both a child
      // and parent are updated
      const topLevelObjectIds = new Set(
        objectIdsWithChange.map((objectIdWithChange) => {
          const topLevelObjectId =
            this.getParentObjectIdWithChangeClosestToRoot(
              objectIdWithChange,
              objectIdsWithChange,
              objects,
            );
          assert(
            isDefined(topLevelObjectId),
            'since we are only passing ids that are in the array, this can never be undefined',
          );
          return topLevelObjectId;
        }),
      );

      this.taskWorkspaceStore.batch(() => {
        topLevelObjectIds.forEach((objectId) => {
          this.updateTransientValue(objectId, objectIdsWithChange, false);
        });
      });
    });
  }

  private getParentObjectIdWithChangeClosestToRoot(
    objectId: number,
    objectIdsWithChange: number[],
    objects: StrictRecord<number, ObjectState>,
  ): number | undefined {
    const object = objects[objectId];
    assert(isDefined(object), 'object id must be defined');

    if (isDefined(object.parentId)) {
      const furthestParentObjectWithChange =
        this.getParentObjectIdWithChangeClosestToRoot(
          object.parentId,
          objectIdsWithChange,
          objects,
        );
      if (isDefined(furthestParentObjectWithChange)) {
        return furthestParentObjectWithChange;
      }
    }

    if (objectIdsWithChange.includes(objectId)) {
      return object.index;
    }

    return undefined;
  }

  private updateTransientValue(
    objectId: number,
    objectIdsWithChanges: number[],
    parentHasChanged: boolean,
    allShapes = getShapesFromShapeInfo(
      this.taskWorkspaceStore.state().outputAnnotationInfo,
    ),
  ) {
    const transientValueStore = this.taskWorkspaceStore('objectTransientInfo')(
      objectId,
      // @ts-ignore -- fix me
    )(this.transientKey);
    const currentIsTransientValue = transientValueStore.state();

    if (parentHasChanged || objectIdsWithChanges.includes(objectId)) {
      this.updateObjectAndDirectChildShapesTransientValue(
        transientValueStore,
        objectId,
        allShapes,
      );
    }

    this.objectScopingService
      .getObjectsInSpecifiedScope(objectId)
      .forEach((childObject) => {
        this.updateTransientValue(
          childObject.index,
          objectIdsWithChanges,
          currentIsTransientValue !== transientValueStore.state(),
          allShapes,
        );
      });
  }

  private updateObjectAndDirectChildShapesTransientValue(
    transientValueStore: Store<AnnotationTransientState[TransientValueKey]>,
    objectId: number,
    allShapes: ShapeState[],
  ) {
    const { objects } = this.taskWorkspaceStore.state();
    const object = objects[objectId];
    if (!object) {
      return;
    }

    transientValueStore.set(
      // @ts-ignore -- fix me
      this.getNewTransientValueForObject(object),
    );

    const shapesInObject = allShapes.filter(
      (shape) => shape.parentId === objectId,
    );
    shapesInObject.forEach((shape) => {
      this.taskWorkspaceStore('shapeTransientInfo')(shape.index)(
        // @ts-ignore -- fix me
        this.transientKey,
        // @ts-ignore -- fix me
      ).set(this.getNewTransientValueForShape(shape));
    });
  }

  protected abstract getObjectIdsChanged$(): Observable<number[]>;
  protected abstract getNewTransientValueForObject(
    object: ObjectState,
  ): AnnotationTransientState[TransientValueKey];
  protected abstract getNewTransientValueForShape(
    shape: ShapeState,
  ): AnnotationTransientState[TransientValueKey];
}
